import { Building, Hash, User } from "@phosphor-icons/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Navigate,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router";
import { useResellerDistrictDetail } from "@/api/reseller/queries";
import { DistrictReadingsSection } from "@/components/districts/district-readings-section";
import { BackButton } from "@/components/navigation/back-button";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { useDistrict } from "@/contexts/district-context";
import { useEffectiveRole } from "@/hooks/use-effective-role";

function InfoCard({
	icon,
	label,
	value,
}: {
	icon: React.ReactNode;
	label: string;
	value: React.ReactNode;
}) {
	return (
		<div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
			<div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
				{icon}
			</div>
			<div className="flex flex-col">
				<span className="text-xs text-muted-foreground">{label}</span>
				<span className="text-sm font-medium">{value}</span>
			</div>
		</div>
	);
}

function InfoCardSkeleton() {
	return (
		<div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
			<Skeleton className="size-9 rounded-md" />
			<div className="flex flex-col gap-1">
				<Skeleton className="h-3 w-16" />
				<Skeleton className="h-4 w-24" />
			</div>
		</div>
	);
}

function ResellerDistrictDetail({
	districtId,
	metersCount,
}: {
	districtId: number;
	metersCount: number;
}) {
	const { data, isLoading } = useResellerDistrictDetail(districtId);
	const { t, i18n } = useTranslation();

	const managerCreatedAt = data?.manager?.created_at
		? new Date(data.manager.created_at).toLocaleDateString(i18n.language, {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			})
		: "—";

	return (
		<>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				{isLoading ? (
					<>
						<InfoCardSkeleton />
						<InfoCardSkeleton />
						<InfoCardSkeleton />
					</>
				) : (
					<>
						<InfoCard
							icon={<User className="size-4" />}
							label={t("districtDetail.manager")}
							value={data?.manager?.company_name ?? "—"}
						/>
						<InfoCard
							icon={<Hash className="size-4" />}
							label={t("districtDetail.meters")}
							value={metersCount}
						/>
						<InfoCard
							icon={<Building className="size-4" />}
							label={t("districtDetail.managerSince")}
							value={managerCreatedAt}
						/>
					</>
				)}
			</div>
			<DistrictReadingsSection districtId={districtId} />
		</>
	);
}

export function DistrictDetailPage() {
	const { name, lang } = useParams<{ name: string; lang: string }>();
	const [searchParams] = useSearchParams();
	const role = useEffectiveRole();
	const navigate = useNavigate();
	const { setDistrict } = useDistrict();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";

	const decodedName = decodeURIComponent(name ?? "");
	const districtIdParam = searchParams.get("id");
	const metersParam = Number(searchParams.get("meters") ?? "0");

	const isReseller = role === "reseller" && districtIdParam;

	useEffect(() => {
		if (!isReseller && decodedName) {
			setDistrict(decodedName);
		}
	}, [isReseller, decodedName, setDistrict]);

	if (!isReseller) {
		return (
			<Navigate
				to={`/${currentLang}/${ROUTE_SLUGS.apartments[currentLang]}`}
				replace
			/>
		);
	}

	function goBack() {
		navigate(`/${currentLang}/${ROUTE_SLUGS.districts[currentLang]}`);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:p-6">
			<div className="flex items-center gap-3">
				<BackButton onClick={goBack} />
				<h1 className="text-lg font-semibold">{decodedName}</h1>
			</div>
			<ResellerDistrictDetail
				districtId={Number(districtIdParam)}
				metersCount={metersParam}
			/>
		</div>
	);
}
